import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TaskMutationParams, LeaderboardPeriodType, Campaign } from "@/types";
import { usePlausible } from "./usePlausible";
import { useEffect, useState, useCallback } from "react";
import { PulsarService } from "@/services/pulsar.service";
import { toast } from "sonner";
import { getErrorMessage } from "@/helpers/getErrorMessage";
import { consumeTaskGuidAndName } from "@/lib/challenges";
import { CAMPAIGN_CONFIG } from "@/config/campaign";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import { useConnection } from "@/contexts/connection-context";
import { useAuthContext } from "@/contexts/auth-context";

export const CHALLENGES_KEYS = {
  all: ["challenges"] as const,
  detail: (id: string, groupId: string) => ["challenges", id, groupId] as const,
  byGroup: (group: string) => ["challenges", "group", group] as const,
  taskStatus: (campaignId: string, groupId: string, address?: string) => ["challenges", "task-status", campaignId, groupId, address || "anonymous"] as const,
  campaignUser: (campaignId: string, groupId: string, address?: string) => ["challenges", "user", campaignId, groupId, address || "anonymous"] as const,
  campaignLeaderboard: (campaignId: string, groupId: string, limit: number, period: LeaderboardPeriodType = LeaderboardPeriodType.ALL) =>
    ["challenges", "leaderboard", campaignId, groupId, limit, period] as const,
  validRevaAccount: (email: string) => ["reva", "valid-account", email] as const,
};

export const useGetCampaigns = () => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.all,
    queryFn: () => PulsarService.getChallengeCampaigns(),
  });
};

export const useGetAllCampaigns = (sortBy?: "participantsAsc" | "participantsDesc") => {
  return useQuery({
    queryKey: [CHALLENGES_KEYS.all, "all", sortBy],
    queryFn: async () => {
      const campaigns = await PulsarService.getAllCampaigns();
      const filteredCampaigns = campaigns.filter((campaign: Campaign) => campaign.isEnabled);

      if (sortBy === "participantsAsc") {
        return filteredCampaigns.sort((a: Campaign, b: Campaign) => a.totalUsers - b.totalUsers);
      }

      if (sortBy === "participantsDesc") {
        return filteredCampaigns.sort((a: Campaign, b: Campaign) => b.totalUsers - a.totalUsers);
      }

      const sortedCampaigns = filteredCampaigns
        .sort((a, b) => {
          const aEndDate = new Date(a.startDate);
          const bEndDate = new Date(b.startDate);
          return bEndDate.getTime() - aEndDate.getTime();
        })
        .filter((campaign) => campaign.isEnabled);

      return sortedCampaigns;
    },
  });
};

export const useGetAllCampaignsByGroup = (group: string) => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.byGroup(group),
    queryFn: async () => {
      const campaigns = await PulsarService.getAllCampaignsByGroup(group);
      return campaigns ?? [];
    },
    enabled: !!group,
  });
};

export const useGetCampaign = (id: string, groupId: string | undefined) => {
  return useQuery({
    queryKey: CHALLENGES_KEYS.detail(id, groupId ?? ""),
    queryFn: () => PulsarService.getCampaign(id, groupId),
  });
};

export const useGetCampaignTaskStatus = (campaignId: string, groupId: string | undefined, isAuthenticated: boolean = true) => {
  const { walletAddress } = useConnection();
  const { accessToken } = useAuthContext();
  const isEnabled = isAuthenticated && !!walletAddress && !!accessToken;

  return useQuery({
    queryKey: CHALLENGES_KEYS.taskStatus(campaignId, groupId || "", walletAddress),
    queryFn: () => {
      return PulsarService.getChallengeCampaignTaskStatus(campaignId, groupId);
    },
    enabled: isEnabled,
    staleTime: 0, // Always consider data stale
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Refetch when component mounts
  });
};

export const useGetCampaignUser = (campaignId: string, groupId: string | undefined, isAuthenticated: boolean = true) => {
  const { walletAddress } = useConnection();
  const { accessToken } = useAuthContext();
  const isEnabled = isAuthenticated && !!walletAddress && !!accessToken;

  return useQuery({
    queryKey: CHALLENGES_KEYS.campaignUser(campaignId, groupId || "", walletAddress),
    queryFn: async () => {
      return await PulsarService.getChallengeCampaignUser(campaignId, groupId);
    },
    enabled: isEnabled,
  });
};

export const useGetCampaignLeaderboard = (
  campaignId: string,
  groupId: string | undefined,
  _address?: string,
  limit: number = 10,
  period: LeaderboardPeriodType = LeaderboardPeriodType.ALL,
  page: number = 0
) => {
  return useQuery({
    queryKey: [...CHALLENGES_KEYS.campaignLeaderboard(campaignId, groupId || "", limit, period), page],
    queryFn: async () => {
      let date;
      if (period === LeaderboardPeriodType.WEEKLY) {
        const today = new Date();
        const dayOfWeek = today.getDay() === 0 ? 7 : today.getDay();
        const diff = 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        date = monday.toISOString();
      }

      return await PulsarService.getChallengeCampaignLeaderboard(campaignId, groupId, page, limit, period, date);
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
  });
};

export const useQuestsMutations = () => {
  const queryClient = useQueryClient();
  const plausible = usePlausible();

  const taskMutation = useMutation({
    mutationFn: async ({ taskGuid, extraArguments, campaignId, groupId }: TaskMutationParams) => {
      try {
        const { data: result } = await PulsarService.doCampaignTask(taskGuid, extraArguments);
        plausible("Airdrop: Do Task", {
          props: { taskGuid },
        });

        if (!result?.status) {
          toast.error(result.error || "Error doing task", {
            duration: 6000,
            dismissible: true,
            style: {
              background: "#fee2e2",
              color: "#991b1b",
              border: "1px solid #fca5a5",
            },
          });
          return null;
        }

        // Immediately fetch the updated task status
        const updatedTaskStatus = await PulsarService.getChallengeCampaignTaskStatus(campaignId, groupId);
        

        queryClient.setQueriesData(
          { queryKey: ["challenges", "task-status", CAMPAIGN_CONFIG.id, CAMPAIGN_CONFIG.groupId] },
          updatedTaskStatus
        );

        return { status: true, updatedTaskStatus, campaignId, groupId };
      } catch (e: unknown) {
        toast.error(getErrorMessage(e as Error, "Error doing task"), {
          duration: 6000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5",
          },
        });
        return null;
      }
    },
    onSuccess: () => {
      // Always invalidate queries to ensure all components get updated data
      queryClient.invalidateQueries({
        queryKey: ["challenges", "task-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", "user"],
      });
    },
  });

  return {
    taskMutation,
  };
};

export const useConsumeQuestsArgs = (campaignId: string, groupId: string | undefined) => {
  const { taskMutation } = useQuestsMutations();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const getAndConsume = (key: string): string | null => {
    const value = searchParams.get(key);
    if (!value) return null;

    const withoutKey = new URLSearchParams(searchParams.toString());
    withoutKey.delete(key);
    const newUrl = withoutKey.toString() ? `${pathname}?${withoutKey.toString()}` : pathname;
    router.replace(newUrl);
    return value;
  };

  const handleConsumeTwitterFollow = useCallback(async () => {
    const value = getAndConsume("twitterAuth");
    if (!value) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5",
        },
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      taskGuid,
    });
  }, [campaignId, groupId, taskMutation, getAndConsume]);

  const handleConsumeTwitterRelogin = useCallback(async (campaignId: string, groupId: string | undefined) => {
    const value = getAndConsume("twitterRelogin");
    if (!value) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5",
        },
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      taskGuid,
    });
  }, [taskMutation, getAndConsume]);

  const handleConsumeDiscordCode = useCallback(async () => {
    const code = getAndConsume("code");
    if (!code) return;

    const { taskGuid, taskName } = consumeTaskGuidAndName();
    if (!taskGuid || !taskName) {
      toast.error("Task Guid not found", {
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5",
        },
      });
      return;
    }

    await taskMutation.mutateAsync({
      campaignId,
      groupId,
      taskName,
      extraArguments: [code],
      taskGuid,
    });
  }, [campaignId, groupId, taskMutation, getAndConsume]);

  const handleDiscordConnectionCallback = useCallback(async () => {
    const code = searchParams.get("code");
    const error = searchParams.get("error");
    
    // Only handle Discord OAuth callbacks (not task completion codes)
    if (!code || error) return;
    
    // Check if this is a Discord OAuth callback by looking for Discord-specific parameters
    const state = searchParams.get("state");
    if (!state || !state.includes("discord")) return;

    try {
      // Refresh social user data after Discord connection
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
      
      toast.success("Discord connected successfully!");
      
      // Clean up URL parameters
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete("code");
      newSearchParams.delete("state");
      const newUrl = newSearchParams.toString() 
        ? `${pathname}?${newSearchParams.toString()}` 
        : pathname;
      router.replace(newUrl);
    } catch (error) {
      console.error("Failed to refresh social user data after Discord connection:", error);
    }
  }, [searchParams, queryClient, pathname, router]);

  useEffect(() => {
    if (!campaignId) return;

    handleConsumeTwitterFollow();
    handleConsumeDiscordCode();
    handleConsumeTwitterRelogin(campaignId, groupId);
    handleDiscordConnectionCallback();
  }, [searchParams, campaignId, groupId, handleConsumeTwitterFollow, handleConsumeDiscordCode, handleConsumeTwitterRelogin, handleDiscordConnectionCallback]);
};
